import { useEffect, useState } from "react";
import { getHost } from "./getHost";

// Identity for the embedded app, sourced from the zoid host's
// `currentUser` prop (Vuex `currentAccount` + user fields). The host
// hydrates this synchronously on its side; for us it lands as soon as
// zoid finishes the iframe handshake, so we poll briefly on first mount
// in case React renders before xprops is ready.

export interface CurrentUser {
  id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  accountId?: string;
  /** Team-level role: owner / admin / editor / viewer. */
  accountRole?: string;
  /** System-level role: 'admin' / 'master' = internal CS / staff. */
  role?: string;
  presenterLanguage?: string;
  planType?: string;
  whiteLabelPresenterDomain?: string;
  whiteLabelLink?: string;
}

const POLL_INTERVAL_MS = 50;
const TIMEOUT_MS = 1500;

export function useCurrentUser(): CurrentUser | null {
  const [user, setUser] = useState<CurrentUser | null>(
    () => (getHost()?.currentUser as CurrentUser | undefined) ?? null,
  );

  useEffect(() => {
    if (user) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + TIMEOUT_MS;

    const tick = () => {
      if (cancelled) return;
      const next = (getHost()?.currentUser as CurrentUser | undefined) ?? null;
      if (next) {
        setUser(next);
        return;
      }
      if (Date.now() >= deadline) return;
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  return user;
}
